import { useState, useRef, useEffect } from "react";
import { useMe, useLogin, useChangePassword, apiGet, apiPost, laufenderBuild, fehlerText } from "@vimaana/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useTeamModel, VGTIcon, type Einsatz, type Hour, type Job } from "./lib/vgt";
import { InstallBanner } from "./components/InstallBanner";
import { LieferscheinSheet } from "./components/LieferscheinSheet";
import {
  VGTHeute, VGTEinsaetze, VGTStunden, VGTGeld, VGTEinsatz, VGTProfil, VGTNotifs, VGTFeedback,
  ZeitSheet, InteresseSheet, NeuSheet, RechnungUploadSheet, FotoUploadSheet,
  AuslagenSheet, AbsageSheet, type FotoZiel, type ZeitZiel,
} from "./vgt-screens";

const APP = "team";

type Layer = { key: number; kind: "einsatz" | "profil" | "notifs" | "feedback"; einsatz?: Einsatz };
type Sheet = { kind: "zeit"; ziel: ZeitZiel } | { kind: "interesse"; job: Job } | { kind: "neu" }
  | { kind: "absage"; einsatz: Einsatz }
  | { kind: "rechnung"; file?: File } | { kind: "ersetzen"; belegId: string }
  | { kind: "fotos"; ziel: FotoZiel }
  | { kind: "lieferschein"; projektId?: string }
  | { kind: "auslagen"; hour: Hour } | null;

export default function App() {
  // Magic-Link: /setup?token=… öffnet den Passwort-Setzen-Fluss, ohne Login.
  const setupToken = (() => {
    try {
      if (window.location.pathname.replace(/\/+$/, "") !== "/setup") return null;
      return new URLSearchParams(window.location.search).get("token");
    } catch { return null; }
  })();
  if (setupToken) return <div className="vgt"><SetupPage token={setupToken} /></div>;
  return <AuthGate />;
}

function AuthGate() {
  const me = useMe(APP);
  if (me.isLoading) return <div className="vgt" style={{ display: "grid", placeItems: "center", color: "var(--text-3)" }}>lädt …</div>;
  if (!me.data) return <div className="vgt"><VGTLogin /></div>;
  if (me.data.must_change_password) return <div className="vgt"><VGTSetPassword /></div>;
  return <div className="vgt"><TeamApp /></div>;
}

// ─────────────────────────── Magic-Link: Passwort über Setup-Link setzen ────
type SetupCheck = { valid: boolean; email?: string; reason?: string };

function SetupPage({ token }: { token: string }) {
  const [check, setCheck] = useState<SetupCheck | null>(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    apiGet<SetupCheck>(`/api/${APP}/auth/setup/${encodeURIComponent(token)}`)
      .then(r => { if (alive) setCheck(r); })
      .catch(() => { if (alive) setCheck({ valid: false, reason: "error" }); });
    return () => { alive = false; };
  }, []);

  const submit = () => {
    setErr("");
    if (pw.length < 12) { setErr("Passwort: mindestens 12 Zeichen."); return; }
    if (pw !== pw2) { setErr("Die beiden Passwörter stimmen nicht überein."); return; }
    setSaving(true);
    apiPost<{ ok: true }>(`/api/${APP}/auth/setup/${encodeURIComponent(token)}`, { new_password: pw })
      .then(() => { window.location.replace("/"); })   // Cookie ist gesetzt → eingeloggt
      .catch(() => { setSaving(false); setErr("Konnte nicht speichern — der Link ist wohl abgelaufen. Melde dich kurz bei uns, wir schicken dir einen neuen."); });
  };

  const Brand = (
    <div className="vgt-login-brand">
      <div className="vgt-logo"><img src="/vimaana-logo.png" alt="Vimaana Garten" /></div>
      <div className="vgt-wordmark"><div className="n">Vimaana Garten</div><div className="tag">Team</div></div>
    </div>
  );

  if (!check) {
    return <div className="vgt-login">{Brand}<div className="vgt-login-tagline">Link wird geprüft …</div></div>;
  }
  if (!check.valid) {
    const msg = check.reason === "used"
      ? "Diesen Link hast du schon benutzt. Passwort vergessen? Sag uns kurz Bescheid, dann kommt ein neuer."
      : check.reason === "expired"
      ? "Dieser Link ist abgelaufen. Melde dich kurz bei uns, dann kommt ein neuer."
      : "Mit diesem Link stimmt etwas nicht. Melde dich kurz bei uns, dann kommt ein neuer.";
    return (
      <div className="vgt-login">
        {Brand}
        <div className="vgt-login-tagline">{msg}</div>
        <form className="vgt-login-form" onSubmit={(e) => { e.preventDefault(); window.location.replace("/"); }}>
          <button className="vgt-login-btn" type="submit">Zur Anmeldung</button>
          <div className="vgt-login-foot">team.vimaanagarten.de</div>
        </form>
      </div>
    );
  }
  return (
    <div className="vgt-login">
      {Brand}
      <div className="vgt-login-tagline">Willkommen{check.email ? `, ${check.email}` : ""}!<br />Vergib jetzt dein eigenes Passwort — danach bist du direkt angemeldet.</div>
      <form className="vgt-login-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        {/* Versteckter Benutzername — verknüpft das Passwort im Passwort-Manager mit der E-Mail. */}
        <input type="email" name="username" autoComplete="username" value={check.email || ""} readOnly hidden />
        <div className="vgt-loginfield">
          <VGTIcon name="lock" />
          <input type="password" autoComplete="new-password" placeholder="Neues Passwort (min. 12 Zeichen)" value={pw} onChange={e => setPw(e.target.value)} autoFocus />
        </div>
        <div className="vgt-loginfield">
          <VGTIcon name="lock" />
          <input type="password" autoComplete="new-password" placeholder="Neues Passwort wiederholen" value={pw2} onChange={e => setPw2(e.target.value)} />
        </div>
        {err && <div className="vgt-login-err">{err}</div>}
        <button className="vgt-login-btn" type="submit" disabled={saving || !pw || !pw2}>
          {saving ? "Speichert …" : "Passwort setzen & los"}
        </button>
        <div className="vgt-login-foot">Dein Browser kann dir ein starkes Passwort vorschlagen und speichern.</div>
      </form>
    </div>
  );
}

// ─────────────────────────── Erst-Login: eigenes Passwort setzen ───────────
function VGTSetPassword() {
  const change = useChangePassword(APP);
  const qc = useQueryClient();
  const me = useMe(APP);
  const email = me.data?.email || "";
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const submit = () => {
    setErr("");
    if (pw.length < 12) { setErr("Neues Passwort: mindestens 12 Zeichen."); return; }
    if (pw !== pw2) { setErr("Die beiden neuen Passwörter stimmen nicht überein."); return; }
    change.mutate({ current_password: cur, new_password: pw }, {
      onSuccess: () => qc.invalidateQueries({ queryKey: ["me", APP] }),
      onError: () => setErr("Das Passwort passt nicht. Lass dir unten einen Link schicken, dann setzt du ein neues."),
    });
  };
  return (
    <div className="vgt-login">
      <div className="vgt-login-brand">
        <div className="vgt-logo"><img src="/vimaana-logo.png" alt="Vimaana Garten" /></div>
        <div className="vgt-wordmark"><div className="n">Vimaana Garten</div><div className="tag">Team</div></div>
      </div>
      <div className="vgt-login-tagline">Fast geschafft! Bitte vergib jetzt dein eigenes Passwort.</div>
      <form className="vgt-login-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        {/* Benutzername für den Passwort-Manager (verknüpft das neue Passwort mit der E-Mail). */}
        <div className="vgt-loginfield">
          <VGTIcon name="person" />
          <input type="email" name="username" autoComplete="username" value={email} readOnly />
        </div>
        <div className="vgt-loginfield">
          <VGTIcon name="lock" />
          <input type="password" autoComplete="current-password" placeholder="Dein bisheriges Passwort" value={cur} onChange={(e) => setCur(e.target.value)} />
        </div>
        <div className="vgt-loginfield">
          <VGTIcon name="lock" />
          <input type="password" autoComplete="new-password" placeholder="Neues Passwort (min. 12 Zeichen)" value={pw} onChange={(e) => setPw(e.target.value)} />
        </div>
        <div className="vgt-loginfield">
          <VGTIcon name="lock" />
          <input type="password" autoComplete="new-password" placeholder="Neues Passwort wiederholen" value={pw2} onChange={(e) => setPw2(e.target.value)} />
        </div>
        {err && <div className="vgt-login-err">{err}</div>}
        <button className="vgt-login-btn" type="submit" disabled={change.isPending || !cur || !pw || !pw2}>
          {change.isPending ? "Speichert …" : "Passwort setzen & los"}
        </button>
        <div className="vgt-login-foot">team.vimaanagarten.de</div>
      </form>
    </div>
  );
}

// ─────────────────────────── Login + Erst-Onboarding (ein Fluss) ──────────
function VGTLogin() {
  const login = useLogin(APP);
  const change = useChangePassword(APP);
  const qc = useQueryClient();
  const [email, setEmail] = useState(() => { try { return localStorage.getItem("vgt_last_email") || ""; } catch { return ""; } });
  const [pw, setPw] = useState("");            // Einmal-/aktuelles Passwort
  const [step, setStep] = useState<"login" | "setpw">("login");
  const [npw, setNpw] = useState("");
  const [npw2, setNpw2] = useState("");
  const [err, setErr] = useState("");
  // "zu" = normaler Login · "offen" = Adresse bestaetigen · "weg" = Mail ist raus
  const [reset, setReset] = useState<"zu" | "offen" | "weg">("zu");
  const [resetLaeuft, setResetLaeuft] = useState(false);

  const doLogin = () => {
    setErr("");
    login.mutate({ email: email.trim(), password: pw }, {
      onSuccess: (res) => {
        try { localStorage.setItem("vgt_last_email", email.trim()); } catch { /* ignore */ }
        if (res?.must_change_password) setStep("setpw");          // im Fluss bleiben, Einmal-PW behalten
        else qc.invalidateQueries({ queryKey: ["me", APP] });
      },
      onError: () => setErr("Das hat nicht geklappt — schau noch mal auf E-Mail und Passwort."),
    });
  };
  const doReset = () => {
    setErr("");
    setResetLaeuft(true);
    // Die Antwort ist absichtlich immer dieselbe — der Server verraet nicht, ob
    // es die Adresse gibt. Deshalb zeigen wir auch bei einem Fehler denselben
    // Text: alles andere waere ein Hinweis, den es nicht geben soll.
    // Ein Eingang für beides: erstes Mal und vergessenes Passwort. Der Server
    // legt das Konto an, wenn die Adresse in der Teamliste steht — und antwortet
    // immer gleich, damit das Formular nicht verrät, wer im Team ist.
    apiPost(`/api/${APP}/auth/zugang`, { email: email.trim() })
      .catch(() => { /* bewusst still */ })
      .finally(() => { setResetLaeuft(false); setReset("weg"); });
  };

  const doSetpw = () => {
    setErr("");
    if (npw.length < 12) { setErr("Neues Passwort: mindestens 12 Zeichen."); return; }
    if (npw !== npw2) { setErr("Die beiden Passwörter stimmen nicht überein."); return; }
    change.mutate({ current_password: pw, new_password: npw }, {
      onSuccess: () => qc.invalidateQueries({ queryKey: ["me", APP] }),
      onError: () => setErr("Konnte nicht speichern — probier es noch mal."),
    });
  };

  return (
    <div className="vgt-login">
      <div className="vgt-login-brand">
        <div className="vgt-logo"><img src="/vimaana-logo.png" alt="Vimaana Garten" /></div>
        <div className="vgt-wordmark"><div className="n">Vimaana Garten</div><div className="tag">Team</div></div>
      </div>
      {step === "login" && reset === "weg" ? (
        <>
          <div className="vgt-login-tagline">Schau in dein Postfach.<br />Wenn wir <b>{email.trim()}</b> im Team haben, ist der Link unterwegs.</div>
          <form className="vgt-login-form" onSubmit={(e) => { e.preventDefault(); setReset("zu"); }}>
            <div className="vgt-login-foot" style={{ marginBottom: 12 }}>
              Er gilt 2 Stunden und nur einmal. Nichts angekommen? Schau auch im Spam —
              die Mail kommt von kontakt@vimaanagarten.de. Sonst sag uns kurz Bescheid.
            </div>
            <button className="vgt-login-btn" type="submit">Zurück zur Anmeldung</button>
          </form>
        </>
      ) : step === "login" && reset === "offen" ? (
        <>
          <div className="vgt-login-tagline">Kein Problem.<br />Wir schicken dir einen Link, mit dem du dein Passwort setzt.</div>
          <form className="vgt-login-form" onSubmit={(e) => { e.preventDefault(); doReset(); }}>
            <div className="vgt-loginfield">
              <VGTIcon name="person" />
              <input type="email" inputMode="email" autoComplete="username" placeholder="Deine E-Mail" value={email} onChange={e => setEmail(e.target.value)} autoFocus />
            </div>
            <div className="vgt-login-foot" style={{ marginBottom: 4 }}>
              Nimm die Adresse, unter der wir dich im Team führen.
            </div>
            <button className="vgt-login-btn" type="submit" disabled={resetLaeuft || !email.trim()}>
              {resetLaeuft ? "Schickt …" : "Link schicken"}
            </button>
            <button type="button" className="vgt-login-textbtn" onClick={() => setReset("zu")}>Doch wieder anmelden</button>
          </form>
        </>
      ) : step === "login" ? (
        <>
          <div className="vgt-login-tagline">Deine Einsätze, deine Stunden, dein Geld.<br />Alles an einem Ort.</div>
          <form className="vgt-login-form" onSubmit={(e) => { e.preventDefault(); doLogin(); }}>
            <div className="vgt-loginfield">
              <VGTIcon name="person" />
              <input type="email" inputMode="email" autoComplete="username" placeholder="E-Mail" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div className="vgt-loginfield">
              <VGTIcon name="lock" />
              <input type="password" autoComplete="current-password" placeholder="Passwort" value={pw} onChange={e => setPw(e.target.value)} />
            </div>
            {err && <div className="vgt-login-err">{err}</div>}
            <button className="vgt-login-btn" type="submit" disabled={login.isPending || !email || !pw}>
              {login.isPending ? "Anmelden …" : <>Anmelden <VGTIcon name="back" style={{ transform: "rotate(180deg)", width: 18, height: 18 }} /></>}
            </button>
            {/* Ein Einstieg für beides. „Passwort vergessen?" allein ließ den
                neuen Helfer ratlos zurück — er hat ja noch keins vergessen. */}
            <button type="button" className="vgt-login-textbtn" onClick={() => { setErr(""); setReset("offen"); }}>
              Zum ersten Mal hier? Passwort vergessen?
            </button>
            <div className="vgt-login-foot">team.vimaanagarten.de</div>
          </form>
          {/* Erst anmelden, dann ablegen — in der Reihenfolge kommt der Helfer
              an. Über dem Formular war der Balken das Lauteste auf der Seite,
              bevor überhaupt klar war, worum es geht. */}
          <InstallBanner />
        </>
      ) : (
        <>
          <div className="vgt-login-tagline">Willkommen, {email}!<br />Vergib jetzt dein eigenes Passwort — danach bist du startklar.</div>
          <form className="vgt-login-form" onSubmit={(e) => { e.preventDefault(); doSetpw(); }}>
            {/* Benutzername (readonly, sichtbar) — verknüpft das neue Passwort im Passwort-Manager mit der E-Mail */}
            <div className="vgt-loginfield">
              <VGTIcon name="person" />
              <input type="email" name="username" autoComplete="username" value={email} readOnly />
            </div>
            <div className="vgt-loginfield">
              <VGTIcon name="lock" />
              <input type="password" autoComplete="new-password" placeholder="Neues Passwort (min. 12 Zeichen)" value={npw} onChange={e => setNpw(e.target.value)} autoFocus />
            </div>
            <div className="vgt-loginfield">
              <VGTIcon name="lock" />
              <input type="password" autoComplete="new-password" placeholder="Neues Passwort wiederholen" value={npw2} onChange={e => setNpw2(e.target.value)} />
            </div>
            {err && <div className="vgt-login-err">{err}</div>}
            <button className="vgt-login-btn" type="submit" disabled={change.isPending || !npw || !npw2}>
              {change.isPending ? "Speichert …" : "Passwort speichern & los"}
            </button>
            <div className="vgt-login-foot">Dein Browser kann dir ein starkes Passwort vorschlagen und es speichern.</div>
          </form>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── Tab-App ──────────────────────────────────────
function TeamApp() {
  const m = useTeamModel();
  const qc = useQueryClient();
  const doLogout = async () => {
    try { await apiPost(`/api/${APP}/auth/logout`); } catch { /* Session ist serverseitig ohnehin entwertet */ }
    qc.clear();
    window.location.replace("/");   // harter Reset → frische Login-Seite, kein Hängenbleiben
  };
  const [tab, setTab] = useState("heute");
  const [stack, setStack] = useState<Layer[]>([]);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [toast, setToast] = useState<string | null>(null);
  const keyRef = useRef(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();

  const showToast = (msg: string) => { setToast(msg); clearTimeout(toastTimer.current); toastTimer.current = setTimeout(() => setToast(null), 2600); };
  const push = (l: Omit<Layer, "key">) => { keyRef.current += 1; setStack(s => [...s, { key: keyRef.current, ...l }]); };
  const pop = () => setStack(s => s.slice(0, -1));

  const onOpenEinsatz = (id: string) => { const e = m.byEinsatz(id); if (e) push({ kind: "einsatz", einsatz: e }); };

  // Aus einer Mitteilung heraus direkt beim betroffenen Einsatz landen.
  // Der Service Worker oeffnet `/?einsatz=<id>`; wir warten, bis das Modell
  // geladen ist, oeffnen den Einsatz und raeumen die Adresse wieder auf —
  // sonst springt ein spaeterer Reload erneut dorthin.
  const deeplinkErledigt = useRef(false);
  useEffect(() => {
    if (deeplinkErledigt.current || m.loading || m.error) return;
    const id = new URLSearchParams(window.location.search).get("einsatz");
    if (!id) { deeplinkErledigt.current = true; return; }
    deeplinkErledigt.current = true;
    const e = m.byEinsatz(id);
    if (e) push({ kind: "einsatz", einsatz: e });
    else setTab("einsaetze");   // schon vorbei oder nicht mehr seins — dann die Liste
    window.history.replaceState({}, "", window.location.pathname);
  }, [m.loading, m.error]);   // eslint-disable-line react-hooks/exhaustive-deps

  const onConfirm = (id: string) => { m.confirmHour(id); showToast("Arbeitszeit bestätigt — danke!"); };
  const onZusage = (id: string) => m.zusageEinsatz(id)
    .then(() => showToast("Schön, dass du dabei bist!"))
    .catch((err: unknown) => showToast((err as Error)?.message || "Hat nicht geklappt — probier es noch mal"));
  // Absage: bei einer offenen Einladung direkt, bei einer Zusage oder Bewerbung
  // erst nachfragen (Grund optional) — dort hängt eine Umplanung dran.
  const onAbsage = (e: Einsatz) => {
    if (e.einsatzStatus === "zugesagt" || e.einsatzStatus === "wartet_auf_inhaber") {
      setSheet({ kind: "absage", einsatz: e });
      return;
    }
    m.absageEinsatz(e.id)
      .then(() => showToast("Alles klar, wir wissen Bescheid"))
      .catch((err: unknown) => showToast((err as Error)?.message || "Hat nicht geklappt — probier es noch mal"));
  };
  const onAuslagen = (h: Hour) => setSheet({ kind: "auslagen", hour: h });
  const onInteresse = (job: Job) => setSheet({ kind: "interesse", job });
  // Zwei Einstiege, ein Blatt: der heutige Einsatz auf der Startseite und die
  // wartende Zeile unter „Stunden" führen beide hierher.
  const onZeitAusEinsatz = (e: Einsatz) => setSheet({
    kind: "zeit",
    ziel: { slotId: e.id, projekt: e.projekt, datum: e.datum, von: e.von, bis: e.bis, pause: e.pauseStd, erstesMal: true },
  });
  const onZeitAusStunde = (h: Hour) => setSheet({
    kind: "zeit",
    ziel: { slotId: h.id, projekt: h.projekt, datum: h.datum, von: h.von, bis: h.bis, pause: h.pause ?? 0, erstesMal: h.status === "zu_bestaetigen" },
  });

  // Vier Reiter statt fünf: „Heute" ist der Einstieg, Plan und offene Stellen
  // liegen zusammen unter „Einsätze" (siehe docs/system/KONZEPT-TEAM-UEBERSICHT.md).
  const TABS = [
    { id: "heute", label: "Heute", icon: "home" },
    { id: "einsaetze", label: "Einsätze", icon: "calendar", badge: m.summary.einladungenOffen },
    { id: "stunden", label: "Stunden", icon: "clock", badge: m.summary.toConfirm },
    { id: "geld", label: "Geld", icon: "wallet" },
  ];

  let screen = null;
  if (m.error) screen = <div className="vgt-screen"><div className="vgt-empty" style={{ marginTop: 60 }}>Dein Konto gehört noch zu keinem von uns im Team.<br />Sag uns kurz Bescheid, dann richten wir das ein.</div></div>;
  else if (tab === "heute") screen = <VGTHeute m={m} onGoTab={setTab} onOpenEinsatz={onOpenEinsatz} onOpenNotifs={() => push({ kind: "notifs" })} onOpenProfil={() => push({ kind: "profil" })} onZeit={onZeitAusEinsatz} onFotos={(ziel) => setSheet({ kind: "fotos", ziel })} unread={m.summary.toConfirm + m.summary.einladungenOffen + m.nachrichtenUngelesen} />;
  else if (tab === "einsaetze") screen = <VGTEinsaetze m={m} onOpenEinsatz={onOpenEinsatz} onZusage={onZusage} onAbsage={onAbsage} onInteresse={onInteresse} />;
  else if (tab === "stunden") screen = <VGTStunden m={m} onConfirm={onConfirm} onZeit={onZeitAusStunde} onAuslagen={onAuslagen} onNeu={() => setSheet({ kind: "neu" })} onFotos={(ziel) => setSheet({ kind: "fotos", ziel })} />;
  else screen = <VGTGeld m={m} onUpload={(file?: File) => setSheet({ kind: "rechnung", file })} onErsetzen={(belegId) => setSheet({ kind: "ersetzen", belegId })} onLieferschein={() => setSheet({ kind: "lieferschein" })} />;

  return (
    <div className="vgt-tabview">
      {screen}
      <div
        aria-hidden
        style={{
          position: "fixed",
          right: 8,
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 62px)",
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.3,
          color: "var(--text-4)",
          pointerEvents: "none",
          zIndex: 5,
        }}
      >
        {/* Kein von Hand gepflegtes „v0.6.2" mehr — das stand zuletzt monatelang
            still, während die App weiterlief. Jetzt das Git-Kürzel des Builds:
            kostet keine Pflege und sagt beim Nachfragen genau, welcher Code läuft. */}
        {laufenderBuild().commit || "dev"}
      </div>
      <div className="vgt-tabbar">
        {TABS.map(t => (
          <button key={t.id} className={"vgt-tab" + (tab === t.id ? " is-on" : "")} onClick={() => setTab(t.id)}>
            <VGTIcon name={t.icon} />
            {!!t.badge && t.badge > 0 && <span className="vgt-tab-badge">{t.badge}</span>}
            {t.label}
          </button>
        ))}
      </div>

      {stack.map(layer => {
        let content = null;
        if (layer.kind === "einsatz" && layer.einsatz) {
          const aktuell = m.byEinsatz(layer.einsatz.id) || layer.einsatz;
          content = <VGTEinsatz einsatz={aktuell} onBack={pop} onFotos={(ziel) => setSheet({ kind: "fotos", ziel })} onLieferschein={(projektId) => setSheet({ kind: "lieferschein", projektId })} onZusage={onZusage} onAbsage={() => onAbsage(aktuell)} busy={m.busy} />;
        }
        else if (layer.kind === "profil") content = <VGTProfil m={m} onBack={pop} onLogout={doLogout} onOpenFeedback={() => push({ kind: "feedback" })} />;
        else if (layer.kind === "notifs") content = <VGTNotifs m={m} onBack={pop} />;
        else if (layer.kind === "feedback") content = <VGTFeedback onBack={pop} />;
        return <div key={layer.key} className="vgt-layer is-enter">{content}</div>;
      })}

      {sheet?.kind === "zeit" && (
        <ZeitSheet ziel={sheet.ziel} busy={m.busy} onClose={() => setSheet(null)}
          onSubmit={(von, bis, pause, notiz) => m.submitZeit(sheet.ziel.slotId, sheet.ziel.datum, von, bis, pause, notiz)
            .then((r: { helfer_status?: string }) => {
              setSheet(null);
              // Der Server entscheidet, ob die Zeit direkt zählt — die App
              // rät das nicht nach, sonst versprechen wir das Falsche.
              showToast(r?.helfer_status === "bestaetigt"
                ? "Zeit ist notiert — zählt in deinem Guthaben."
                : "Danke — wir schauen kurz drauf.");
            })
            .catch((e: unknown) => showToast(fehlerText(e)))} />
      )}
      {sheet?.kind === "auslagen" && (
        <AuslagenSheet h={sheet.hour} busy={m.busy} onClose={() => setSheet(null)}
          onSubmit={(betrag, note) => m.submitAuslagen(sheet.hour, betrag, note).then(() => { setSheet(null); showToast("Auslagen gespeichert"); }).catch(() => showToast("Hat nicht geklappt — probier es noch mal"))} />
      )}
      {sheet?.kind === "absage" && (
        <AbsageSheet einsatz={sheet.einsatz} busy={m.busy} onClose={() => setSheet(null)}
          onSubmit={(grund) => m.absageEinsatz(sheet.einsatz.id, grund).then(() => { setSheet(null); showToast("Abgesagt — wir wissen Bescheid"); }).catch((e) => showToast((e as Error)?.message || "Absage fehlgeschlagen"))} />
      )}
      {sheet?.kind === "interesse" && (
        <InteresseSheet job={sheet.job} busy={m.busy} onClose={() => setSheet(null)}
          onSubmit={() => m.submitBewerbung(sheet.job.id).then(() => { setSheet(null); showToast("Danke — wir teilen ein und melden uns"); }).catch(() => showToast("Hat nicht geklappt — probier es noch mal"))} />
      )}
      {sheet?.kind === "neu" && (
        <NeuSheet projekte={m.projekte} projektpreise={m.projektpreise} busy={m.busy} onClose={() => setSheet(null)}
          onSubmit={(body) => m.submitNeu(body).then(() => { setSheet(null); setTab("stunden"); showToast("Eingetragen — wir schauen drauf"); }).catch(() => showToast("Hat nicht geklappt — probier es noch mal"))} />
      )}
      {sheet?.kind === "rechnung" && (
        <RechnungUploadSheet busy={m.rechnungBusy} initialFile={sheet.file} projektpreise={m.projektpreise} onClose={() => setSheet(null)}
          onSubmit={(args) => m.submitRechnung(args).then(() => { setSheet(null); showToast("Rechnung ist bei uns — danke"); }).catch((e) => showToast(e?.message || "Upload hat nicht geklappt"))} />
      )}
      {sheet?.kind === "ersetzen" && (
        <RechnungUploadSheet ersetzen busy={m.ersetzenBusy} onClose={() => setSheet(null)}
          onErsetzen={(file) => m.submitErsetzen(sheet.belegId, file).then(() => { setSheet(null); showToast("Rechnung ersetzt"); }).catch((e) => showToast(e?.message || "Hat nicht geklappt — probier es noch mal"))} />
      )}
      {sheet?.kind === "lieferschein" && (
        <LieferscheinSheet projekte={m.aktiveProjekte} initialProjektId={sheet.projektId}
          busy={m.lieferscheinBusy} onClose={() => setSheet(null)}
          onSubmit={(args) => m.submitLieferschein(args).then(() => { setSheet(null); showToast("Lieferschein ist bei uns — danke"); }).catch((e) => showToast((e as Error)?.message || "Upload hat nicht geklappt"))} />
      )}
      {sheet?.kind === "fotos" && (
        <FotoUploadSheet projekte={m.fotoProjekte} initialProjektId={sheet.ziel.projektId}
          slotId={sheet.ziel.slotId} datum={sheet.ziel.datum} busy={m.fotoBusy} onClose={() => setSheet(null)}
          onSubmit={(args) => m.submitFotos(args).then((r) => { setSheet(null); showToast(`${r.count} ${r.count === 1 ? "Foto" : "Fotos"} hochgeladen — danke!`); }).catch((e) => showToast(e?.message || "Upload fehlgeschlagen"))} />
      )}

      {toast && <div className="vgt-toast"><span className="ic">✓</span>{toast}</div>}
    </div>
  );
}
